import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { getUserDepots } from '../store/selectors/user';
import useQueryState from './useQueryState';
import { DATE_FORMAT } from '../utils/query';

const isPastDisable = import.meta.env.VITE_DATEPICKER_ENABLE_PAST !== 'true';

export const useDepotAndDateChangeAction = (
  onDepotChangeTrackAction,
  onDateChangeTrackAction
) => {
  const depots = useSelector(getUserDepots);
  const [{ depot, date }, update] = useQueryState(
    useMemo(
      () => [
        {
          name: 'depot',
          decode: (value) =>
            depots.find((depot) => depot.depotCode === value)?.depotCode,

          encode: (value) =>
            depots.find((depot) => depot.depotCode === value)?.depotCode,
        },
        {
          name: 'date',
          decode: (value) => {
            const returnValue = moment.utc(value, DATE_FORMAT);
            const isInvalidDate =
              !returnValue.isValid() ||
              (isPastDisable && returnValue.diff(moment.utc(), 'days') < 0);

            if (isInvalidDate) {
              return null;
            }

            return returnValue;
          },
          encode: (value) => value.format(DATE_FORMAT),
        },
      ],
      [depots]
    )
  );

  const handleDepotChange = useCallback(
    (depot) => {
      update({ depot, date }, { extend: false });
      onDepotChangeTrackAction && onDepotChangeTrackAction(depot);
    },
    [update, date, onDepotChangeTrackAction]
  );

  const handleDateChange = useCallback(
    (date) => {
      update({ date, depot }, { extend: false });
      onDateChangeTrackAction &&
        onDateChangeTrackAction(date.format(DATE_FORMAT));
    },
    [update, depot, onDateChangeTrackAction]
  );

  return {
    depot,
    date,
    updateDepot: handleDepotChange,
    updateDate: handleDateChange,
  };
};
